import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { getAmChartsLocale } from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";

const VehicleMarkStats = ({ item }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();

  const vehicleColor = "#4E7CFF";

  const processData = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [
        {
          vehicleName: "Нет данных",
          displayName: "Нет данных",
          count: 0,
          percent: 0,
          uniqueId: "no-data",
        },
      ];
    }

    return data.map((vehicle, index) => {
      const uniqueId = `${vehicle.vehicleName}_${index}`;

      return {
        vehicleName: vehicle.vehicleName,
        displayName: vehicle.vehicleName,
        count: vehicle.count,
        percent: vehicle.percent,
        uniqueId: uniqueId,
      };
    });
  };

  const processedData = useMemo(() => {
    return processData(item);
  }, [item]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-vehicle-mark-stats");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelX: "zoomY",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    let yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "uniqueId",
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 30,
          inversed: true,
        }),
      }),
    );

    yAxis.get("renderer").labels.template.adapters.add("text", function (text, target) {
      const dataItem = target.dataItem;
      if (dataItem) {
        const data = dataItem.dataContext;
        return data.displayName;
      }
      return text;
    });

    yAxis.data.setAll(processedData);

    let xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 100,
        renderer: am5xy.AxisRendererX.new(root, {}),
      }),
    );

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomY",
        yAxis: yAxis,
      }),
    );

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      visible: true,
    });

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      visible: true,
    });

    xAxis.setAll({
      min: 0,
      max: 100,
      strictMinMax: true,
      numberFormat: "#'%'",
    });

    let vehicleSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Vehicle",
        xAxis: xAxis,
        yAxis: yAxis,
        categoryYField: "uniqueId",
        valueXField: "percent",
      }),
    );

    vehicleSeries.columns.template.setAll({
      fill: am5.color(vehicleColor),
      stroke: am5.color(vehicleColor),
      maxHeight: 80,
    });

    vehicleSeries.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText: `{vehicleName}: {count} ({percent}%)`,
      }),
    );

    vehicleSeries.bullets.push(function () {
      return am5.Bullet.new(root, {
        locationX: 0.5,
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: "{percent}%",
          fill: am5.color("#FFFFFF"),
          fontSize: 11,
          centerX: am5.p50,
          centerY: am5.p50,
          populateText: true,
        }),
      });
    });

    vehicleSeries.data.setAll(processedData);

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, currentLanguage]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.fleetUtilizationRate")}</span>
      </div>

      <div className={cn(style.area__namesMobile)}>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: vehicleColor }}></div>
          <div className={style.area_type}>{t("analytics.vehicles")}</div>
        </div>
      </div>

      <div className={style.area}>
        <div id="chartdiv-vehicle-mark-stats" className={style.area_chart}></div>
        <div className={style.area_names}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: vehicleColor }}></div>
            <div className={style.area_type}>{t("analytics.vehicles")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleMarkStats;
